import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { trackPageView } from "@/lib/analytics";

import Nav from "@/components/landing/Nav";
import Hero from "@/components/landing/Hero";
import WhyCheckRate from "@/components/landing/WhyCheckRate";
import HowItWorks from "@/components/landing/HowItWorks";
import MeetJason from "@/components/landing/MeetJason";
import FAQ from "@/components/landing/FAQ";
import PrivacyTrust from "@/components/landing/PrivacyTrust";
import FinalCTA from "@/components/landing/FinalCTA";
import Footer from "@/components/landing/Footer";
import BDMProgram from "@/pages/BDMProgram";
import TeamLeaders from "@/pages/TeamLeaders";
import Apply from "@/pages/Apply";
import Admin from "@/pages/Admin";
import BDMReport from "@/pages/BDMReport";
import Library from "@/pages/Library";
import LibraryShare from "@/pages/LibraryShare";
import LibraryBundleShare from "@/pages/LibraryBundleShare";
import BDMApply from "@/pages/BDMApply";
import MLOApply from "@/pages/MLOApply";
import MLOProgram from "@/pages/MLOProgram";
import MLOLanding from "@/pages/MLOLanding";
import ToolkitPage from "@/pages/ToolkitPage";
import ScrollToHash from "@/components/ScrollToHash";
import PartnerOnboarding from "@/pages/PartnerOnboarding";
import FieldUpdatePage from "@/pages/FieldUpdatePage";
import MLOLogin from "@/pages/MLOLogin";
import MLODashboard from "@/pages/MLODashboard";
import MLOLibrary from "@/pages/MLOLibrary";
import MLODirectory from "@/pages/MLODirectory";
import BDMLogin from "@/pages/BDMLogin";
import BDMDashboard from "@/pages/BDMDashboard";
import BDMAccess from "@/pages/BDMAccess";
import BDMPacket from "@/pages/BDMPacket";
import PlatformLanding from "@/pages/PlatformLanding";
import AuthCallback from "@/pages/AuthCallback";
import PlaybookShare from "@/pages/PlaybookShare";
import RosterDashboard from "@/pages/RosterDashboard";
import AnnualRateCheck from "@/pages/AnnualRateCheck";
import { capturePartnerCode } from "@/lib/attribution";
import { ApplyIntentProvider } from "@/components/ApplyIntent";
import { MLOProvider } from "@/components/MLOProvider";
import { RateCheckRouterProvider } from "@/components/RateCheckRouter";
// Google auth returns at {origin}{path}#session_id=... — this must be checked
// during render (not in an effect) so the exchange happens before any page
// runs its own auth check.
function AuthHashGate({ children }) {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return children;
}

function RouteChangeTracker() {
  const location = useLocation();
  useEffect(() => {
    capturePartnerCode();
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

// A mistyped or double-pasted link (//bdmlogin) doesn't match any route and
// would quietly render the consumer page. Collapse repeated slashes instead.
function SlashNormalizer() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const clean = location.pathname.replace(/\/{2,}/g, "/");
    if (clean !== location.pathname) {
      navigate(clean + location.search + location.hash, { replace: true });
    }
  }, [location.pathname, location.search, location.hash, navigate]);
  return null;
}



function LandingPage() {
  return (
    <div data-testid="landing-page" className="min-h-screen bg-linen text-navy antialiased">
      <Nav />
      <main>
        <Hero />
        <WhyCheckRate />
        <HowItWorks />
        <MeetJason />
        <FAQ />
        <PrivacyTrust />
        <FinalCTA />
      </main>
      <Footer />
      <Toaster
        position="top-center"
        richColors
        toastOptions={{
          className: "font-body",
        }}
      />
    </div>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ApplyIntentProvider>
         <RateCheckRouterProvider>
          <RouteChangeTracker />
          <SlashNormalizer />
          <ScrollToHash />
          <AuthHashGate>
          <Routes>
            <Route path="/" element={<PlatformLanding />} />
            <Route path="/apply" element={<Apply />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/partnerreports" element={<BDMReport />} />
            <Route path="/library" element={<Library />} />
            <Route path="/mlos" element={<MLODirectory />} />
            <Route path="/library/share/folder/:token" element={<LibraryBundleShare kind="folder" />} />
            <Route path="/library/share/subcategory/:token" element={<LibraryBundleShare kind="subcategory" />} />
            <Route path="/library/share/:token" element={<LibraryShare />} />
            <Route path="/partnerapply" element={<BDMApply />} />
            <Route path="/roster" element={<RosterDashboard />} />
            <Route path="/annualratecheck" element={<AnnualRateCheck />} />
            <Route path="/annual" element={<Navigate to="/annualratecheck" replace />} />
            {/* Attributed variant — /jason/partnerapply auto-tags submissions to that MLO */}
            <Route path="/:slug/partnerapply" element={<BDMApply />} />
            {/* Per-MLO branded apply page — same template, their name/photo/application link */}
            {/* Bookmarkable link reference pages — /toolkit/recruiting etc. */}
            <Route path="/toolkit/:key" element={<ToolkitPage />} />
            <Route path="/partneronboarding" element={<PartnerOnboarding />} />
            <Route path="/playbook/:token" element={<PlaybookShare />} />
            <Route path="/fieldupdate/:id" element={<FieldUpdatePage />} />
            <Route path="/:slug/apply" element={<MLOProvider><Apply /></MLOProvider>} />
            <Route path="/mlogrowth" element={<MLOApply />} />
            <Route path="/mloprogram" element={<MLOProgram />} />
            <Route path="/mlologin" element={<MLOLogin />} />
            <Route path="/mlodashboard" element={<MLODashboard />} />
            <Route path="/mlodashboard/library" element={<MLOLibrary />} />
            <Route path="/bdmlogin" element={<BDMLogin />} />
            <Route path="/partnerlogin" element={<Navigate to="/bdmlogin" replace />} />
            <Route path="/bdmdashboard" element={<BDMDashboard />} />
            <Route path="/bdmdashboard/packet" element={<BDMPacket />} />
            <Route path="/p/:token" element={<BDMAccess />} />
            {/* Backwards-compat redirect from the older slug */}
            <Route path="/mlogrowthapply" element={<Navigate to="/mlogrowth" replace />} />
            {/* Backwards-compat redirects from older slugs */}
            <Route path="/bdmmlo-report" element={<Navigate to="/partnerreports" replace />} />
            <Route path="/bdm-report" element={<Navigate to="/partnerreports" replace />} />
            <Route path="/report" element={<Navigate to="/partnerreports" replace />} />
            <Route path="/partnerprogram" element={<BDMProgram />} />
            <Route path="/partnerprogram.html" element={<BDMProgram />} />
            <Route path="/brokerage" element={<BDMProgram />} />
            <Route path="/brokerage.html" element={<BDMProgram />} />
            <Route path="/partner" element={<BDMProgram />} />
            <Route path="/partner.html" element={<BDMProgram />} />
            <Route path="/mlo" element={<BDMProgram />} />
            <Route path="/mlo.html" element={<BDMProgram />} />
            <Route path="/program" element={<BDMProgram />} />
            <Route path="/program.html" element={<BDMProgram />} />
            <Route path="/teamleaders" element={<TeamLeaders />} />
            <Route path="/teamleaders.html" element={<TeamLeaders />} />
            {/* Backwards-compat redirects from the old /bdmprogram slug */}
            <Route path="/bdmprogram" element={<Navigate to="/partnerprogram" replace />} />
            <Route path="/bdmprogram.html" element={<Navigate to="/partnerprogram.html" replace />} />
            {/* Per-MLO branded landing pages — MUST be last, catches all remaining single-segment paths */}
            <Route path="/:slug" element={<MLOLanding />} />
            <Route path="*" element={<LandingPage />} />
          </Routes>
          </AuthHashGate>
         </RateCheckRouterProvider>
        </ApplyIntentProvider>
      </BrowserRouter>
    </div>
  );
}
